import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { LanguageProvider } from 'i11n/client'
import ErrorBoundary from 'components/ErrorHandler'
import 'core-js/features/array/at'
import App, {
  type AppContext,
  type AppInitialProps,
  type AppProps,
} from 'next/app'
import Head from 'next/head'
import { useMemo, useState } from 'react'
import { Toaster } from 'ui'
import 'ui/styles/global.css'
import '../styles/globals.css'
import { IS_DEV } from '../config/config.exporter'
import { createPortal } from 'react-dom'
import { GoogleTagManager } from '@next/third-parties/google'
import { LanguageISO6391 } from 'ai-constants'
import {
  InspectionLanguageSource,
  resolveAppLanguage,
  resolveRequestLanguage,
} from 'lib/resolve-inspection-language'

function MyApp({
  Component,
  pageProps,
}: Pick<AppProps, 'Component'> & {
  pageProps: AppProps['pageProps'] & {
    inspection?: InspectionLanguageSource
    requestLanguage?: LanguageISO6391
  }
}) {
  const [queryClient] = useState(() => new QueryClient())
  const inspectionLanguage = useMemo(
    () =>
      resolveAppLanguage({
        inspection: pageProps.inspection,
        requestLanguage: pageProps.requestLanguage,
      }),
    [pageProps.inspection, pageProps.requestLanguage],
  )

  return (
    <>
      <LanguageProvider lang={inspectionLanguage}>
        <Head>
          <meta charSet='utf-8' />
          <meta httpEquiv='X-UA-Compatible' content='IE=edge' />
          <meta
            name='viewport'
            content='width=device-width,initial-scale=1,minimum-scale=1,maximum-scale=1,user-scalable=no,viewport-fit=cover'
          />
          <meta name='description' content='Autoinspector' />

          {!IS_DEV && (
            <script
              dangerouslySetInnerHTML={{
                __html: `
              (function(h,o,t,j,a,r){
                h.hj=h.hj||function(){(h.hj.q=h.hj.q||[]).push(arguments)};
                h._hjSettings={hjid:3322418,hjsv:6};
                a=o.getElementsByTagName('head')[0];
                r=o.createElement('script');r.async=1;
                r.src=t+h._hjSettings.hjid+j+h._hjSettings.hjsv;
                a.appendChild(r);
            })(window,document,'https://static.hotjar.com/c/hotjar-','.js?sv=');
              `,
              }}
            />
          )}

          <link rel='icon' href='/assets/icon-192x192.png?v=2' />
        </Head>
        <ErrorBoundary>
          <QueryClientProvider client={queryClient}>
            <Component {...pageProps} />
            {process.env.NEXT_PUBLIC_GTM && (
              <GoogleTagManager gtmId={process.env.NEXT_PUBLIC_GTM} />
            )}
            {typeof window !== 'undefined' &&
              createPortal(
                <div className='z-[99999]'>
                  <Toaster size='lg' />
                </div>,
                document.body,
              )}
          </QueryClientProvider>
        </ErrorBoundary>
      </LanguageProvider>
    </>
  )
}

MyApp.getInitialProps = async (
  appContext: AppContext,
): Promise<AppInitialProps> => {
  const appProps = await App.getInitialProps(appContext)
  const clientAcceptLanguage =
    typeof window !== 'undefined' ? navigator.languages.join(',') : undefined
  const requestLanguage = resolveRequestLanguage(
    appContext.ctx.req?.headers['accept-language'] || clientAcceptLanguage,
  )

  return {
    ...appProps,
    pageProps: {
      requestLanguage,
      ...appProps.pageProps,
    },
  }
}

export default MyApp
